import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  Cell,
} from 'recharts';
import AdminContext from '../../../../../context/admin';
import Tooltip from '../../../../../atomicDesign/molecules/ChartToolTip';

type MonthKey = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
type YearKey = string;

type AdminContextType = {
  language?: {
    global?: {
      monthChartText: (typeof defaultMonthNames)[number][];
    };
    eosDashboard: {
      avgMaxKwp: string;
      meteringPoint: string;
      baselineChart: {
        total: string;
        average: string;
        meteringPoint: string;
        missingData: string;
        missingDataDescription: string;
      };
    };
    buildings: {
      details: {
        baseline: {
          title: string;
        };
      };
    };
  };
};

export type BaselineChartData = {
  meteringPointId?: string;
  monthlyAverages: Record<string, number | string>;
  monthlyYearlyAverages: Record<string, Record<YearKey, number | string>>;
  monthlyMaxAvg: Record<string, number | string>;
};

type Props = {
  baseline: BaselineChartData | null | undefined;
  height?: number;
};

const defaultMonthNames = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

const barColors = ['#2B7FFF', '#60a5fa', '#93c5fd', '#bfdbfe'] as const;
const baselineColor = '#69BF4A';
const maxAvgLineColor = '#ef4444';

function clamp(n: number, min: number, max: number) {
  return Math.max(min, Math.min(max, n));
}

function toNumber(x: unknown): number | null {
  const n = typeof x === 'string' ? Number(x) : typeof x === 'number' ? x : NaN;
  return Number.isFinite(n) ? n : null;
}

function normalizeMonthKey(key: string): MonthKey | null {
  const num = Number(key);
  if (Number.isFinite(num)) {
    const m = Math.trunc(num);
    if (m >= 1 && m <= 12) return m as MonthKey;
  }

  const k = key.trim().toLowerCase();
  for (let i = 0; i < defaultMonthNames.length; i++) {
    const short = defaultMonthNames[i].toLowerCase();
    if (k === short) return (i + 1) as MonthKey;
    if (k.startsWith(short)) return (i + 1) as MonthKey;
  }
  return null;
}

function formatKwh(n: number) {
  return `${n.toFixed(1)} kWh`;
}
function formatKwp(n: number) {
  return `${n.toFixed(1)} kWp`;
}

const CustomLeftTick: React.FC<{
  x?: number;
  y?: number;
  payload?: any;
  fontSize?: number;
}> = ({ x, y, payload, fontSize = 11 }) => (
  <g transform={`translate(${x ?? 0},${y ?? 0})`}>
    <text textAnchor="end" fill="#64748b" fontSize={fontSize} fontWeight={500}>
      <tspan x={0} dy="0">
        {Number(payload?.value ?? 0).toFixed(0)}
      </tspan>
      <tspan x={0} dy="1.2em">
        kWh
      </tspan>
    </text>
  </g>
);

const CustomRightTick: React.FC<{
  x?: number;
  y?: number;
  payload?: any;
  fontSize?: number;
}> = ({ x, y, payload, fontSize = 11 }) => (
  <g transform={`translate(${x ?? 0},${y ?? 0})`}>
    <text
      textAnchor="start"
      fill="#64748b"
      fontSize={fontSize}
      fontWeight={500}
    >
      <tspan x={0} dy="0">
        {Number(payload?.value ?? 0).toFixed(0)}
      </tspan>
      <tspan x={0} dy="1.2em">
        kWp
      </tspan>
    </text>
  </g>
);

type ChartRow = {
  monthIndex: MonthKey;
  month: (typeof defaultMonthNames)[number];
  baseline: number | null;
  maxAvg: number | null;
  missing: boolean;
  years: Record<YearKey, number | null>;
};

const CustomBaselineTooltip: React.FC<any> = ({ active, payload, label }) => {
  const { language } = useContext(AdminContext) as AdminContextType;
  if (!active || !payload || payload.length === 0) return null;

  const row: ChartRow | undefined = payload?.[0]?.payload;

  const barEntries = payload.filter((p: any) =>
    String(p.dataKey).startsWith('years.')
  );

  const baselineEntry = payload.find((p: any) => p.dataKey === 'baseline');
  const maxAvgEntry = payload.find((p: any) => p.dataKey === 'maxAvg');

  return (
    <Tooltip>
      <Tooltip.Time>{label}</Tooltip.Time>

      {row?.missing && (
        <Tooltip.Section>
          <Tooltip.Row
            label={
              language?.eosDashboard?.baselineChart?.missingData ??
              'Missing data'
            }
            value={
              language?.eosDashboard?.baselineChart?.missingDataDescription ??
              'One or more values are missing for this month'
            }
            markColor="#ef4444"
          />
        </Tooltip.Section>
      )}

      <Tooltip.Section>
        {barEntries.map((entry: any) => {
          const year = String(entry.dataKey).replace('years.', '');
          return (
            <Tooltip.Row
              key={String(entry.dataKey)}
              label={year}
              value={entry.value == null ? '—' : formatKwh(Number(entry.value))}
              markColor={entry.color}
            />
          );
        })}
      </Tooltip.Section>

      <Tooltip.Section>
        {baselineEntry && (
          <Tooltip.Row
            label={language?.buildings?.details?.baseline?.title ?? 'Baseline'}
            value={
              baselineEntry.value == null
                ? '—'
                : formatKwh(Number(baselineEntry.value))
            }
            markColor={baselineEntry.color}
          />
        )}

        {maxAvgEntry && (
          <Tooltip.Row
            label={language?.eosDashboard?.avgMaxKwp ?? 'Average max kWp'}
            value={
              maxAvgEntry.value == null
                ? '—'
                : formatKwp(Number(maxAvgEntry.value))
            }
            markColor={maxAvgEntry.color}
          />
        )}
      </Tooltip.Section>
    </Tooltip>
  );
};

export default function BaselineChart({ baseline, height = 300 }: Props) {
  const { language } = useContext(AdminContext) as AdminContextType;

  // ✅ measure actual card/container width
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [w, setW] = useState(900);

  useEffect(() => {
    if (!wrapperRef.current) return;

    const el = wrapperRef.current;
    const obs = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect?.width;
      if (typeof next === 'number' && Number.isFinite(next)) setW(next);
    });

    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const monthNames = language?.global?.monthChartText ?? defaultMonthNames;

  const computed = useMemo(() => {
    if (!baseline) {
      return {
        years: [] as string[],
        data: [] as ChartRow[],
        baselineTotal: 0,
        avgMaxAvg: 0,
        minMaxAvg: 0,
        maxMaxAvg: 0,
      };
    }

    const yearSet = new Set<string>();
    for (const [, yearObj] of Object.entries(
      baseline.monthlyYearlyAverages ?? {}
    )) {
      if (yearObj && typeof yearObj === 'object') {
        Object.keys(yearObj).forEach((y) => yearSet.add(String(y)));
      }
    }
    const years = Array.from(yearSet).sort();

    const baselineByMonth = new Map<MonthKey, number | null>();
    const maxAvgByMonth = new Map<MonthKey, number | null>();
    const yearlyByMonth = new Map<MonthKey, Record<string, number | null>>();

    for (const [k, v] of Object.entries(baseline.monthlyAverages ?? {})) {
      const m = normalizeMonthKey(k);
      if (m) baselineByMonth.set(m, toNumber(v));
    }

    for (const [k, v] of Object.entries(baseline.monthlyMaxAvg ?? {})) {
      const m = normalizeMonthKey(k);
      if (m) maxAvgByMonth.set(m, toNumber(v));
    }

    for (const [k, yearObj] of Object.entries(
      baseline.monthlyYearlyAverages ?? {}
    )) {
      const m = normalizeMonthKey(k);
      if (!m) continue;

      const out: Record<string, number | null> = Object.fromEntries(
        years.map((y) => [y, null])
      );

      if (yearObj && typeof yearObj === 'object') {
        for (const [y, val] of Object.entries(yearObj)) {
          out[String(y)] = toNumber(val);
        }
      }

      yearlyByMonth.set(m, out);
    }

    const data: ChartRow[] = Array.from(
      { length: 12 },
      (_, i) => (i + 1) as MonthKey
    ).map((m) => {
      const monthLabel = monthNames[m - 1];
      const baselineVal = baselineByMonth.get(m) ?? null;
      const maxAvgVal = maxAvgByMonth.get(m) ?? null;

      const yearVals =
        yearlyByMonth.get(m) ??
        (Object.fromEntries(years.map((y) => [y, null])) as Record<
          string,
          number | null
        >);

      const allYearsMissing =
        years.length > 0 && years.every((y) => yearVals[y] == null);
      const missing =
        baselineVal == null || maxAvgVal == null || allYearsMissing;

      return {
        monthIndex: m,
        month: monthLabel,
        baseline: baselineVal,
        maxAvg: maxAvgVal,
        missing,
        years: yearVals,
      };
    });

    const baselineTotal = data.reduce((sum, r) => sum + (r.baseline ?? 0), 0);

    const maxAvgValues = data
      .map((r) => r.maxAvg)
      .filter((v): v is number => v != null);
    const avgMaxAvg = maxAvgValues.length
      ? maxAvgValues.reduce((sum, v) => sum + v, 0) / maxAvgValues.length
      : 0;

    const minMaxAvg = maxAvgValues.length ? Math.min(...maxAvgValues) : 0;
    const maxMaxAvg = maxAvgValues.length ? Math.max(...maxAvgValues) : 0;

    return { years, data, baselineTotal, avgMaxAvg, minMaxAvg, maxMaxAvg };
  }, [baseline, monthNames]);

  if (
    !baseline ||
    !baseline.monthlyAverages ||
    !baseline.monthlyYearlyAverages ||
    !baseline.monthlyMaxAvg
  ) {
    return null;
  }

  // ✅ responsive rules based on container width (NOT window)
  const yearsCount = computed.years.length || 1;

  // "scale" goes 0..1 roughly between 360px and 900px
  const scale = clamp((w - 360) / (900 - 360), 0, 1);

  const axisFont = Math.round(9 + 2 * scale); // 9..11
  const xTickFont = Math.round(9 + 2 * scale); // 9..11
  const legendFont = Math.round(10 + 2 * scale); // 10..12

  const leftAxisWidth = Math.round(60 + 30 * scale); // 60..90
  const rightAxisWidth = Math.round(60 + 30 * scale); // 60..90

  const chartMargin = {
    top: 12,
    right: Math.round(10 + 20 * scale), // 10..30
    bottom: Math.round(18 + 10 * scale), // 18..28
    left: Math.round(10 + 10 * scale), // 10..20
  };

  // Keep bars "fat enough" but still fit the card.
  // If there are many years, reduce bar size.
  const baseBar = 18 - (yearsCount - 1) * 2; // 18,16,14,12...
  const barSize = clamp(Math.round(baseBar * (0.75 + 0.25 * scale)), 10, 18);

  const barGap = clamp(Math.round(2 + 2 * scale), 1, 4); // 1..4
  const barCategoryGap = `${Math.round(18 + 12 * scale)}%`; // 18%..30%

  // X labels: keep them unless it’s *really* tight
  const rotateX = w < 460;
  const xInterval = w < 420 ? 1 : 0; // show every 2nd only when super small

  const marginKwp = (computed.maxMaxAvg - computed.minMaxAvg) * 0.2;

  return (
    <div ref={wrapperRef} style={{ width: '100%' }}>
      <p
        style={{
          textAlign: 'center',
          fontSize: '0.875rem',
          color: '#64748b',
          fontWeight: 500,
          marginBottom: 12,
        }}
      >
        {language?.eosDashboard?.baselineChart?.total ?? 'Total'}:{' '}
        {computed.baselineTotal.toFixed(1)} kWh |{' '}
        {language?.eosDashboard?.baselineChart?.average ?? 'Average'}:{' '}
        {computed.avgMaxAvg.toFixed(1)} kWp
      </p>

      <ResponsiveContainer width="100%" height={height}>
        <ComposedChart
          data={computed.data}
          barCategoryGap={barCategoryGap}
          barGap={barGap}
          margin={chartMargin}
        >
          <CartesianGrid stroke="#f1f5f9" vertical={false} opacity={0.7} />

          <XAxis
            dataKey="month"
            axisLine={false}
            tickLine={false}
            interval={xInterval}
            tick={{
              fill: '#64748b',
              fontSize: xTickFont,
              fontWeight: 500,
            }}
            tickMargin={rotateX ? 14 : 10}
            angle={rotateX ? -20 : 0}
            textAnchor={rotateX ? 'end' : 'middle'}
            height={rotateX ? 34 : 24}
          />

          <YAxis
            yAxisId="left"
            tick={<CustomLeftTick fontSize={axisFont} />}
            width={leftAxisWidth}
            axisLine={false}
            tickLine={false}
          />

          <YAxis
            yAxisId="right"
            orientation="right"
            tick={<CustomRightTick fontSize={axisFont} />}
            width={rightAxisWidth}
            domain={[0, computed.maxMaxAvg + marginKwp]}
            axisLine={false}
            tickLine={false}
          />

          <RechartsTooltip content={<CustomBaselineTooltip />} />

          <Legend
            wrapperStyle={{ fontSize: legendFont }}
            iconSize={Math.round(8 + 2 * scale)} // 8..10
          />

          {computed.years.map((year, index) => (
            <Bar
              key={year}
              dataKey={`years.${year}`}
              name={year}
              fill={barColors[index % barColors.length]}
              barSize={barSize}
              maxBarSize={barSize}
              yAxisId="left"
              isAnimationActive={false}
            >
              {computed.data.map((row) => (
                <Cell
                  key={`${year}-${row.monthIndex}`}
                  fillOpacity={row.missing ? 0.25 : 1}
                />
              ))}
            </Bar>
          ))}

          <Line
            type="monotone"
            dataKey="baseline"
            stroke={baselineColor}
            strokeWidth={clamp(2 + scale * 0.8, 2, 2.8)}
            dot={{
              r: clamp(2.5 + scale * 1, 2.5, 3.5),
              fill: baselineColor,
              stroke: baselineColor,
            }}
            name={language?.buildings?.details?.baseline?.title ?? 'Baseline'}
            yAxisId="left"
            connectNulls={false}
            isAnimationActive={false}
          />

          <Line
            type="monotone"
            dataKey="maxAvg"
            stroke={maxAvgLineColor}
            strokeWidth={clamp(2 + scale * 0.8, 2, 2.8)}
            dot={{
              r: clamp(2.5 + scale * 1, 2.5, 3.5),
              fill: maxAvgLineColor,
              stroke: maxAvgLineColor,
            }}
            name={language?.eosDashboard?.avgMaxKwp ?? 'max kWp'}
            yAxisId="right"
            connectNulls={false}
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>

      {baseline.meteringPointId && (
        <p
          style={{
            textAlign: 'center',
            margin: '12px 0 0',
            fontSize: '0.75rem',
            color: '#94a3b8',
            fontWeight: 500,
          }}
        >
          {language?.eosDashboard?.meteringPoint ?? 'Metering Point:'}{' '}
          {baseline.meteringPointId}
        </p>
      )}
    </div>
  );
}
